import { Download } from "lucide-react";
import type { ReactNode } from "react";
import type { CMSComponentProps } from "../engine/CMSComponent.tsx";
import { isExternal, type Resume } from "../types.ts";
import { buttonVariants } from "./ui/button.tsx";

/*
 * The résumé: /cv (public, from the CMS "resume" global) and every résumé PDF
 * (server/resumePdf.ts renders ResumeDocument and prints it with Chromium). No scroll
 * animations here, so every section is visible in print; print: classes and the
 * @media print block in styles.css give black on white.
 */

const ResumeSection = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="mt-8 print:mt-5">
    {/* sections flow across pages; only a heading is kept with what follows it */}
    <h2 className="mb-3 border-b border-border pb-1.5 text-sm font-medium tracking-widest text-muted-foreground uppercase break-after-avoid print:mb-2">
      {title}
    </h2>
    {children}
  </section>
);

/** Paragraphs from a plain-text field; blank lines separate them. */
const Paragraphs = ({ text }: { text: string }) => (
  <div className="space-y-2 text-[15px] leading-relaxed text-foreground/80 print:text-[11pt] print:leading-snug">
    {text
      .split(/\n\s*\n/)
      .filter((part) => part.trim())
      .map((part, index) => (
        <p key={index} className="whitespace-pre-line">
          {part.trim()}
        </p>
      ))}
  </div>
);

export function ResumeDocument({ resume }: { resume: Resume }) {
  const contact = [resume.location, resume.email, resume.phone].filter(Boolean);

  return (
    <article className="print-resume">
      <header>
        <h1 className="text-3xl font-bold tracking-tight text-foreground print:text-[22pt]">{resume.name}</h1>
        {resume.headline && <p className="mt-1 text-lg text-foreground/80 print:text-[13pt]">{resume.headline}</p>}
        {(contact.length > 0 || resume.links.length > 0) && (
          <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {contact.map((item) => (
              <span key={item}>{item}</span>
            ))}
            {resume.links.map((link) => (
              <a
                key={link.href}
                href={link.href}
                {...(isExternal(link.href) ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                className="underline decoration-foreground/30 underline-offset-2 hover:text-foreground"
              >
                {link.label}
              </a>
            ))}
          </p>
        )}
      </header>

      {resume.summary && (
        <ResumeSection title="Summary">
          <Paragraphs text={resume.summary} />
        </ResumeSection>
      )}

      {resume.skills.length > 0 && (
        <ResumeSection title="Skills">
          <ul className="flex flex-wrap gap-1.5 print:block">
            {resume.skills.map((skill, index) => (
              <li
                key={skill}
                className="rounded bg-muted px-2 py-0.5 text-sm text-foreground/80 print:inline print:bg-transparent print:p-0 print:text-[10.5pt]"
              >
                {skill}
                <span className="hidden print:inline">{index < resume.skills.length - 1 ? ", " : ""}</span>
              </li>
            ))}
          </ul>
        </ResumeSection>
      )}

      {resume.experience.length > 0 && (
        <ResumeSection title="Experience">
          <div className="space-y-6 print:space-y-4">
            {resume.experience.map((role) => (
              <div key={role.id} className="break-inside-avoid">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                  <h3 className="font-semibold text-foreground">
                    {role.title}
                    {role.company && <span className="font-normal text-foreground/80">, {role.company}</span>}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {[role.dates, role.location].filter(Boolean).join(" | ")}
                  </p>
                </div>
                {role.summary && (
                  <div className="mt-1.5">
                    <Paragraphs text={role.summary} />
                  </div>
                )}
                {role.bullets.length > 0 && (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-[15px] leading-relaxed text-foreground/80 print:space-y-0.5 print:text-[10.5pt] print:leading-snug">
                    {role.bullets.map((bullet, index) => (
                      <li key={index}>{bullet}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </ResumeSection>
      )}

      {resume.education && (
        <ResumeSection title="Education">
          <Paragraphs text={resume.education} />
        </ResumeSection>
      )}

      {resume.additional && (
        <ResumeSection title="Additional">
          <Paragraphs text={resume.additional} />
        </ResumeSection>
      )}
    </article>
  );
}

/** /cv, built by server/pages.ts from the CMS "resume" global. */
export function ResumePage({ node }: CMSComponentProps) {
  const resume = node.resume as Resume;
  return (
    <div className="mx-auto max-w-3xl px-6 pt-28 pb-16 print:max-w-none print:p-0">
      <div className="mb-8 flex justify-end print:hidden">
        <a href="/cv.pdf" download className={buttonVariants({ variant: "outline", size: "sm" })}>
          <Download />
          Download PDF
        </a>
      </div>
      <ResumeDocument resume={resume} />
    </div>
  );
}
